import { useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Messages } from 'primereact/messages';
import { Toast } from 'primereact/toast';
import { useQuery } from 'react-query';
import { ProgressBar } from 'primereact/progressbar';
import { getAllGrants, getAllPlaces } from '../../../services/acl';
import AddRoleForm from '../AddRoleForm';
import { useCreateRole } from '../../../services/mutations';

function DialogFooter({ onCancel = () => {}, onSubmit = () => {}, submitting = false, loading = false }) {
  return (
    <div>
      <Button label="Cancel" onClick={onCancel} size="small" text />
      <Button label="Submit" loading={submitting} disabled={loading} onClick={onSubmit} size="small" />
    </div>
  );
}

function AddRoleModal({ open = false, onClose = () => {} }) {
  const {
    isFetching: loadingGrants,
    data: allGrants,
    refetch: loadGrants,
  } = useQuery('allGrants', getAllGrants, { enabled: false });
  const {
    isFetching: loadingPlaces,
    data: allPlaces,
    refetch: loadPlaces,
  } = useQuery('allPlaces', getAllPlaces, { enabled: false });
  const formRef = useRef<HTMLFormElement>(null);
  const resultToast = useRef<Toast>(null);
  const messages = useRef<Messages>(null);
  const createRole = useCreateRole();

  const closeModal = () => {
    onClose();
    formRef.current?.reset();
    createRole.reset();
  };

  const submit = () => formRef.current?.requestSubmit();

  const handleSubmit = (data: any) => {
    createRole.mutate(data, {
      onSuccess: () => {
        resultToast.current?.show({
          severity: 'success',
          summary: 'New role',
          detail: 'Role was successfully created.',
          life: 3000,
        });
        closeModal();
      },
      onError: (err) => {
        messages.current?.show({ severity: 'error', summary: 'Unable to add new role', sticky: true });
      },
    });
  };

  const handleCancel = () => {
    closeModal();
  };

  useEffect(() => {
    if (open) {
      loadGrants();
      loadPlaces();
    }
  }, [open, loadGrants, loadPlaces]);

  const loadingData = loadingGrants || loadingPlaces;

  return (
    <>
      <Toast ref={resultToast} />
      <Dialog
        header="Add new role"
        onHide={handleCancel}
        footer={
          <DialogFooter
            onCancel={handleCancel}
            onSubmit={submit}
            submitting={createRole.isLoading}
            loading={loadingData}
          />
        }
        visible={open}
        breakpoints={{ '600px': '100vw' }}
        style={{ width: '450px' }}
        modal
      >
        <Messages ref={messages} />
        {loadingData ? (
          <ProgressBar mode="indeterminate" style={{ height: '4px' }} />
        ) : (
          <AddRoleForm
            onSubmit={handleSubmit}
            ref={formRef}
            grantsData={allGrants || []}
            placesData={allPlaces || []}
          />
        )}
      </Dialog>
    </>
  );
}

export default AddRoleModal;
